import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  NOTE_MAX_LENGTH,
  isNoteEditable,
  updateNote,
  deleteNote,
} from "../../../services/bookingNotesService";

function BookingNoteItem({
  note,
  currentUserId,
  now,
  formatTimestamp,
  onUpdated,
  onDeleted,
}) {
  const { t } = useTranslation();
  const canModify = isNoteEditable(note, currentUserId, now);
  const [isEditing, setIsEditing] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [draft, setDraft] = useState(note.text);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const editRef = useRef(null);

  useEffect(() => {
    if (!canModify) {
      setIsEditing(false);
      setIsConfirmingDelete(false);
      setDraft(note.text);
      setError("");
    }
  }, [canModify, note.text]);

  useEffect(() => {
    if (isEditing && editRef.current) {
      editRef.current.focus();
      const length = editRef.current.value.length;
      editRef.current.setSelectionRange(length, length);
    }
  }, [isEditing]);

  const handleStartEdit = () => {
    setIsConfirmingDelete(false);
    setDraft(note.text);
    setError("");
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setDraft(note.text);
    setError("");
  };

  const handleSave = async () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed.length > NOTE_MAX_LENGTH || saving) {
      return;
    }
    setSaving(true);
    setError("");
    try {
      const updated = await updateNote({
        id: note.id,
        text: trimmed,
        createdBy: currentUserId,
        createdAt: note.createdAt,
      });
      setIsEditing(false);
      onUpdated(updated);
    } catch (err) {
      setError(err?.message || t("Unable to update note."));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (saving) {
      return;
    }
    setSaving(true);
    setError("");
    try {
      await deleteNote({
        id: note.id,
        createdBy: currentUserId,
        createdAt: note.createdAt,
      });
      onDeleted(note.id);
    } catch (err) {
      setError(err?.message || t("Unable to delete note."));
      setSaving(false);
    }
  };

  const remainingOk = draft.trim().length > 0 && draft.length <= NOTE_MAX_LENGTH;

  return (
    <article className="bd-notes-item">
      <div className="bd-notes-item-meta">
        <span className="bd-notes-item-user">{note.createdBy}</span>
        {note.createdAt ? (
          <span className="bd-notes-item-time">{formatTimestamp(note.createdAt)}</span>
        ) : null}
      </div>

      {isEditing ? (
        <div className="bd-notes-item-edit">
          <textarea
            ref={editRef}
            className="bd-notes-textarea"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            maxLength={NOTE_MAX_LENGTH}
            rows={3}
            aria-label={t("Edit note")}
          />
          <div className="bd-notes-item-edit-footer">
            <span className="bd-notes-counter">
              {draft.length}/{NOTE_MAX_LENGTH}
            </span>
            <div className="bd-notes-item-edit-actions">
              <button
                type="button"
                className="bd-notes-text-btn"
                onClick={handleCancelEdit}
                disabled={saving}
              >
                {t("Cancel")}
              </button>
              <button
                type="button"
                className="bd-notes-primary-btn"
                onClick={handleSave}
                disabled={saving || !remainingOk || draft.trim() === note.text.trim()}
              >
                {t("Save")}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <p className="bd-notes-item-body">{note.text}</p>
      )}

      {canModify && !isEditing && (
        <div className="bd-notes-item-toolbar">
          {isConfirmingDelete ? (
            <div className="bd-notes-item-confirm">
              <span>{t("Delete this note?")}</span>
              <button
                type="button"
                className="bd-notes-text-btn"
                onClick={() => setIsConfirmingDelete(false)}
                disabled={saving}
              >
                {t("Cancel")}
              </button>
              <button
                type="button"
                className="bd-notes-danger-btn"
                onClick={handleDelete}
                disabled={saving}
              >
                {t("Delete")}
              </button>
            </div>
          ) : (
            <>
              <span className="bd-notes-item-hint">
                {t("Edit or delete within 2 minutes")}
              </span>
              <div className="bd-notes-item-icons">
                <button
                  type="button"
                  className="bd-notes-icon-btn"
                  onClick={handleStartEdit}
                  aria-label={t("Edit note")}
                >
                  <i className="fas fa-pencil-alt" aria-hidden="true"></i>
                </button>
                <button
                  type="button"
                  className="bd-notes-icon-btn bd-notes-icon-btn-danger"
                  onClick={() => setIsConfirmingDelete(true)}
                  aria-label={t("Delete note")}
                >
                  <i className="fas fa-trash-alt" aria-hidden="true"></i>
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {error ? <p className="bd-notes-item-error">{error}</p> : null}
    </article>
  );
}

export default BookingNoteItem;
